import { Button, styled } from "@mui/material";
import {
  createContext,
  Dispatch,
  Fragment,
  FunctionComponent,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from "react";
import Icon from "@mui/material/Icon";
import {
  DEFAULT_SIGN_IN_VALUE,
  DEFAULT_SIGN_UP_VALUE,
} from "../../../theme/defaultConstants";
import { iframe } from "../../helpers/environment";
import { useConfiguration } from "../../dataAccess/api/configuration";
import { useNavigateSameTab } from "../../helpers/navigation";
import { pageMapping } from "../../builder/mapping/page";
import { Alignment } from "../../types/theme";
import { useUser } from "../../dataAccess/api/user";
import { ThemeButton } from "../ui/ThemeComponents/ThemeButton";
import { PublicComponentProps } from "./PublicComponentProps";
import {
  MediaSetting,
  getJustificationByAlignment,
} from "../../builder/themeOptionsBuilder";
import { TransparentButton } from "../ui/TransparentButton";
import { Container } from "../ui/DefaultPageContainer";
import { BackButton } from "../ui/BackButton";
import Banner, { BannerConfiguration } from "../ui/Banner";
import OnlyMobile from "../ui/OnlyMobile";
import OnlyDesktop from "../ui/OnlyDesktop";
import { EditorCtx } from "../../wysiwyg/EditorCtx";
import PageNavigation from "../ui/PageNavigation";
import type { NavigationComponentProps } from "../pages/PDP";
import { LinkWrapper } from "../ui/anchor/LinkWrapper";
import {
  profileDataDesktopAttribute,
  profileDataMobileAttribute,
} from "../common/LoginAnchorButton";
import ProfileAccessModal, {
  useProfileSettings,
} from "../common/ProfileAccessModal";
import ProfileInfo from "../ui/ProfileInfo";
import { defaultFocusOutline } from "../../helpers/accessibility";

interface Settings extends BannerConfiguration {
  desktopTopSpacing?: string;
  desktopBottomSpacing?: string;
}

type Props = PublicComponentProps<Settings> & NavigationComponentProps;

interface ProfileState {
  loginOpen: boolean;
  setLoginOpen: Dispatch<SetStateAction<boolean>>;
  signUpOpen: boolean;
  setSignUpOpen: Dispatch<SetStateAction<boolean>>;
}

const defaultProfileState: ProfileState = {
  loginOpen: false,
  setLoginOpen: () => {},
  signUpOpen: false,
  setSignUpOpen: () => {},
};

const MainContainer = styled(Banner)<
  { isUserLoggedIn?: boolean } & Pick<
    Settings,
    "desktopBottomSpacing" | "desktopTopSpacing"
  >
>(({ theme, desktopTopSpacing, desktopBottomSpacing }) => {
  const {
    [theme.breakpoints.up("lg")]: lg,
    [theme.breakpoints.down("md")]: md,
    ...medias
  } = theme.getMediaForSettings(() => ({
    paddingTop:
      desktopTopSpacing !== undefined
        ? `${desktopTopSpacing}`
        : MediaSetting.top,
    paddingBottom:
      desktopBottomSpacing !== undefined
        ? `${desktopBottomSpacing}`
        : MediaSetting.top,
  }));
  return {
    display: "flex",
    minHeight: "4rem",
    fontWeight: "bold",
    alignItems: "center",
    [theme.breakpoints.down("lg")]: {
      paddingLeft: `${theme.defaultSpacing.md.page.edges}`,
      paddingRight: `${theme.defaultSpacing.md.page.edges}`,
      ...lg,
    },
    [theme.breakpoints.down("md")]: {
      paddingTop: theme.settings.layout?.mobile.top,
      paddingBottom: theme.settings.layout?.mobile.top, // this should be top because both paddings should be the same
      ...md,
    },
    ...medias,
  };
});

const StyledBackButton = styled(BackButton)(() => ({
  padding: 0,
  justifySelf: "left",
  minWidth: "fit-content",
}));

const StyledButtonPopup = styled(ThemeButton)(() => ({
  marginLeft: "1rem",
}));

const ProfileLoggedInButton = styled(TransparentButton)(({ theme }) => ({
  marginLeft: "1rem",
  "&:hover": {
    background: "transparent",
  },
  "&.Mui-focusVisible": {
    outline: defaultFocusOutline(theme),
  },
}));

export const StyledIconButton = styled(Button)(({ theme }) => ({
  padding: 0,
  minWidth: "fit-content",
  "&.Mui-focusVisible": {
    outline: defaultFocusOutline(theme),
    outlineOffset: 3,
  },
}));

const StyledOnlyDesktop = styled(OnlyDesktop)(() => ({
  width: "100%",
}));

const StyledButton = styled(ThemeButton)(() => ({
  marginLeft: "1rem",
}));

const MobileContainer = styled(OnlyMobile)<{ isProfileFullWidth?: boolean }>(
  ({ isProfileFullWidth }) => ({
    display: "flex",
    width: "100%",
    paddingLeft: isProfileFullWidth ? "0rem" : "1.5rem",
    paddingRight: isProfileFullWidth ? "0rem" : "1.5rem",
    paddingTop: "0.75rem",
    paddingBottom: isProfileFullWidth ? "0rem" : "0.75rem",
  }),
);

const ProfileContainer = styled("div")<{
  alignment?: Alignment;
}>(({ alignment, theme }) => ({
  flex: 1,
  marginLeft: "1rem",
  display: "flex",
  alignItems: "center",
  justifyContent: getJustificationByAlignment(alignment || "right"),
  [theme.breakpoints.up("md")]: {
    position: "relative",
    flexDirection: "row",
  },
}));

const StyledIcon = styled(Icon)<{
  textColor: Settings["layout"]["textColor"];
}>(({ textColor }) => ({
  color: `${textColor || "inherit"} !important`,
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  padding: `0 ${theme.defaultSpacing.md.page.edges} !important`,
  alignItems: "center",
}));

export function useProfileState(): ProfileState {
  const [loginOpen, setLoginOpen] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);
  return {
    loginOpen,
    setLoginOpen,
    signUpOpen,
    setSignUpOpen,
  };
}

export const ProfileContext = createContext({ ...defaultProfileState });
const Profile: FunctionComponent<Props> = ({
  backButtonIn,
  isBackButtonIn,
  navigation,
  settings: { layout },
  item,
}) => {
  const { configuration } = useConfiguration();
  const { push: navigate } = useNavigateSameTab();
  const { loginSettings, signupSettings } = useProfileSettings();
  const { user: realUser, loadingUser } = useUser();
  const { mocks } = useContext(EditorCtx);
  let user = realUser;
  if (mocks && "noUserMock" in mocks) {
    user = mocks?.noUserMock;
  }

  const mobileProfileAccessRef = useRef<HTMLButtonElement | null>(null);
  const [mobileProfileAccessOpen, setMobileProfileAccessOpen] = useState(false);

  const [desktopSignInEl, setDesktopSignInEl] =
    useState<HTMLButtonElement | null>(null);
  const [desktopSignUpEl, setDesktopSignUpEl] =
    useState<HTMLButtonElement | null>(null);

  const { loginOpen, setLoginOpen, signUpOpen, setSignUpOpen } =
    useContext(ProfileContext);
  const containerProps = {
    alignment: layout?.layoutAlignment,
  };
  const expanded = loginOpen || signUpOpen;
  const { contained } = layout || {};
  const Wrapper = contained ? Container : Fragment;
  const signUpButtonTarget = iframe ? { target: "_parent" } : {};
  const showNavigation = !!(isBackButtonIn && backButtonIn === "Profile");

  const signUpLabel = signupSettings?.buttonLabel || DEFAULT_SIGN_UP_VALUE;
  const signInLabel = loginSettings?.buttonLabel || DEFAULT_SIGN_IN_VALUE;
  const signUpFormLabel = signupSettings?.label || DEFAULT_SIGN_UP_VALUE;
  const signInFormLabel = loginSettings?.label || DEFAULT_SIGN_IN_VALUE;

  return (
    <Wrapper>
      {loadingUser ? (
        <ProfileInfo loading textColor={layout?.textColor} user={user} />
      ) : (
        <MainContainer
          layout={layout}
          {...{ [profileDataDesktopAttribute]: true }}
          {...containerProps}
          isUserLoggedIn={user?.confirmed}
        >
          <>
            <MobileContainer isProfileFullWidth={contained}>
              {showNavigation && (
                <LinkWrapper
                  href={pageMapping.PLP.path}
                  passHref
                  legacyBehavior
                >
                  <StyledBackButton textColor={layout?.textColor} />
                </LinkWrapper>
              )}
              <ProfileContainer {...containerProps}>
                {!configuration?.ssoSetting.forceJwtAuth ? (
                  <>
                    <StyledIconButton
                      {...{ [profileDataMobileAttribute]: true }}
                      ref={mobileProfileAccessRef}
                      aria-label={signUpFormLabel}
                      onClick={() => setMobileProfileAccessOpen(true)}
                      disableRipple
                      aria-expanded={mobileProfileAccessOpen}
                    >
                      <StyledIcon textColor={layout?.textColor}>
                        person
                      </StyledIcon>
                    </StyledIconButton>
                    {mobileProfileAccessOpen && (
                      <ProfileAccessModal
                        defaultAction="signUp"
                        itemSlug={item?.slug}
                        textColor={layout?.textColor}
                        anchorEl={mobileProfileAccessRef.current}
                        onClose={() => setMobileProfileAccessOpen(false)}
                        desktop={false}
                      />
                    )}
                  </>
                ) : (
                  <StyledIconButton
                    disableRipple
                    onClick={() =>
                      navigate(configuration.ssoSetting.signInRedirectUrl)
                    }
                    aria-expanded={mobileProfileAccessOpen}
                  >
                    <StyledIcon textColor={layout?.textColor}>
                      person
                    </StyledIcon>
                  </StyledIconButton>
                )}
              </ProfileContainer>
            </MobileContainer>

            <StyledOnlyDesktop>
              <>
                <StyledContainer>
                  {showNavigation && (
                    <PageNavigation
                      navigation={{
                        type: navigation,
                        links: [{ label: item?.name || "" }],
                      }}
                      textColor={layout.textColor}
                    />
                  )}
                  {!user?.id ? (
                    <ProfileContainer {...containerProps}>
                      {!configuration?.ssoSetting.forceJwtAuth ? (
                        <>
                          <StyledButtonPopup
                            ref={setDesktopSignUpEl}
                            aria-label={signUpFormLabel}
                            onClick={() => setSignUpOpen(true)}
                            aria-expanded={expanded}
                          >
                            {signUpLabel}
                          </StyledButtonPopup>
                          {signUpOpen && (
                            <ProfileAccessModal
                              defaultAction="signUp"
                              itemSlug={item?.slug}
                              textColor={layout?.textColor}
                              anchorEl={desktopSignUpEl}
                              onClose={() => setSignUpOpen(false)}
                              desktop
                            />
                          )}
                        </>
                      ) : (
                        <ThemeButton
                          href={configuration.ssoSetting.signUpRedirectUrl}
                          {...signUpButtonTarget}
                          customVariant="secondary"
                        >
                          {signUpLabel}
                        </ThemeButton>
                      )}
                      {!configuration?.ssoSetting.forceJwtAuth ? (
                        <>
                          <StyledButtonPopup
                            ref={setDesktopSignInEl}
                            aria-label={signInFormLabel}
                            onClick={() => setLoginOpen(true)}
                            aria-expanded={expanded}
                          >
                            {signInLabel}
                          </StyledButtonPopup>
                          {loginOpen && (
                            <ProfileAccessModal
                              defaultAction="signIn"
                              itemSlug={item?.slug}
                              textColor={layout?.textColor}
                              anchorEl={desktopSignInEl}
                              onClose={() => setLoginOpen(false)}
                              desktop
                            />
                          )}
                        </>
                      ) : (
                        <StyledButton
                          href={configuration.ssoSetting.signInRedirectUrl}
                          {...signUpButtonTarget}
                        >
                          {signInLabel}
                        </StyledButton>
                      )}
                    </ProfileContainer>
                  ) : (
                    <ProfileContainer {...containerProps}>
                      <>
                        <ProfileLoggedInButton
                          ref={setDesktopSignInEl}
                          aria-label={signInFormLabel}
                          onClick={() => setLoginOpen(true)}
                          disableRipple
                          data-analytics-amra="no-track"
                          aria-expanded={expanded}
                        >
                          <ProfileInfo
                            textColor={layout?.textColor}
                            user={user}
                          />
                        </ProfileLoggedInButton>
                        {loginOpen && (
                          <ProfileAccessModal
                            textColor={layout?.textColor}
                            itemSlug={item?.slug}
                            anchorEl={desktopSignInEl}
                            onClose={() => setLoginOpen(false)}
                            desktop
                          />
                        )}
                      </>
                    </ProfileContainer>
                  )}
                </StyledContainer>
              </>
            </StyledOnlyDesktop>
          </>
        </MainContainer>
      )}
    </Wrapper>
  );
};

export default Profile;
